import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Check, Clock, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Button, Surface, Text } from '@/components/flup';
import { cn } from '@/lib/utils';
import { useAppDispatch, useAppSelector } from '@/hooks/useReduxHooks';
import { RootState } from '@/store';
import {
  getMonetisationPlans,
  getVenueMonetisation,
  subscribeVenue,
  switchVenuePlan,
} from '@/store/thunkMidddleware/monetisationMiddleware';
import {
  BillingCycle,
  CyclePricing,
  VenueMonetisation,
  formatPaise,
  isAwaitingPayment,
  isPlanChangeLocked,
  isZeroCommission,
  planLabel,
} from '@/types/monetisationTypes';

/**
 * Switching a venue between commission and subscription.
 *
 * The server decides what is allowed and when, so this screen reports rather
 * than reasons: `switchEligibility` gates the action, and the three switch
 * outcomes each mean something different to the vendor —
 *
 *   PAYMENT_REQUIRED  nothing changed yet; go and authorise a mandate
 *   SCHEDULED         auto-debit is already off, the flip lands at period end
 *   SWITCHED          done, effective now
 *
 * A scheduled switch is deliberately not offered as an undo. Requesting it
 * cancels the auto-debit mandate there and then, and nothing short of a fresh
 * authorisation brings that back — so calling off the switch alone would strand
 * the venue on a subscription that can never renew and is no longer scheduled to
 * move off. The vendor keeps the period they paid for and re-subscribes after it.
 *
 * A venue can also be locked out of switching entirely — a FOUNDING_PARTNER
 * grant runs zero-commission for a fixed term and reverts on its own. There the
 * screen shows the term and the server's message and offers no action at all,
 * because every switch call would be refused anyway.
 */

const formatDate = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : '';

interface PlanSwitcherModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  venueId: string | null;
  venueName?: string;
  /** Hands back the Razorpay subscription checkout options to open. */
  onSubscriptionCheckout: (checkout: Record<string, any>) => void;
}

const PlanSwitcherModal: React.FC<PlanSwitcherModalProps> = ({
  open,
  onOpenChange,
  venueId,
  venueName,
  onSubscriptionCheckout,
}) => {
  const dispatch = useAppDispatch();
  const { plansResponse, venuePlans, venuePlanResponse, switchPlanResponse, subscribeResponse } =
    useAppSelector((state: RootState) => state.monetisation);

  const current: VenueMonetisation | undefined = venueId ? venuePlans[venueId] : undefined;
  const plans = plansResponse?.payload;
  const cycles: CyclePricing[] = plans?.subscription?.cycles || [];

  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!open || !venueId) return;
    setReason('');
    dispatch(getMonetisationPlans({}));
    dispatch(getVenueMonetisation({ venueId }));
  }, [open, venueId]);

  useEffect(() => {
    if (current?.billingCycle) setBillingCycle(current.billingCycle);
  }, [current?.billingCycle]);

  const onSubscription = current?.plan === 'SUBSCRIPTION';
  const eligibility = current?.switchEligibility;
  // An unfinished payment is not a booked switch: it gets the plan picker and
  // "pay and switch" again, not "keep my current plan".
  const awaitingPayment = isAwaitingPayment(current);
  const pending = awaitingPayment ? undefined : current?.pendingPlanChange;
  const founding = current?.foundingPartner;
  // Locked means no plan change of any kind, in either direction — unlike
  // `eligibility.canSwitch`, which only ever describes leaving a subscription.
  const locked = isPlanChangeLocked(current);
  const zeroCommission = isZeroCommission(current?.plan);

  const selectedCycle = useMemo(
    () => cycles.find((c) => c.billingCycle === billingCycle),
    [cycles, billingCycle],
  );

  const busy = switchPlanResponse?.isLoading || subscribeResponse?.isLoading;

  const refresh = () => {
    if (venueId) dispatch(getVenueMonetisation({ venueId }));
  };

  /** Creates the Razorpay subscription and hands checkout back to the caller. */
  const startSubscription = (cycle: BillingCycle) => {
    if (!venueId) return;
    dispatch(
      subscribeVenue({
        venueId,
        billingCycle: cycle,
        callback: (res: any) => {
          if (res?.payload?.checkout) {
            if (res.payload.resumed) {
              toast.info('Picking up where your last payment left off');
            }
            onOpenChange(false);
            onSubscriptionCheckout(res.payload.checkout);
          } else if (res?.error) {
            toast.error(res.error);
          }
        },
      }),
    );
  };

  const goToSubscription = () => {
    if (!venueId) return;
    dispatch(
      switchVenuePlan({
        venueId,
        toPlan: 'SUBSCRIPTION',
        billingCycle,
        callback: (res: any) => {
          const outcome = res?.payload?.outcome;
          // 202 is not a failure — it means "now go and pay".
          if (outcome === 'PAYMENT_REQUIRED') {
            startSubscription(billingCycle);
          } else if (outcome) {
            toast.success(res?.message || 'Plan updated');
            refresh();
            onOpenChange(false);
          } else if (res?.error) {
            toast.error(res.error);
          }
        },
      }),
    );
  };

  const goToCommission = () => {
    if (!venueId) return;
    dispatch(
      switchVenuePlan({
        venueId,
        toPlan: 'COMMISSION',
        reason: reason.trim() || undefined,
        callback: (res: any) => {
          if (res?.payload?.outcome) {
            toast.success(res?.message || 'Plan updated');
            refresh();
            onOpenChange(false);
          } else if (res?.error) {
            // 409 carries the "you can switch from <date>" explanation.
            toast.error(res.error);
            refresh();
          }
        },
      }),
    );
  };

  const cycleButton = (selected: boolean) =>
    cn(
      'rounded-lg border p-3 text-left transition-colors',
      selected
        ? 'border-flup-brand bg-flup-brand-soft'
        : 'border-flup-hairline hover:bg-flup-surface-alt',
    );

  const loadingFirst = venuePlanResponse?.isLoading && !current;

  /* Under a scheduled move to pay per booking. Two things the vendor needs and
     neither is an action: nothing changes before the date, and the way back
     onto zero commission is a fresh subscription, not an undo. */
  const keepZeroCommissionNote = current?.currentPeriodEnd
    ? `Nothing changes before then — zero commission runs to ${formatDate(
        current.currentPeriodEnd,
      )}. To keep it, subscribe again after that date.`
    : 'Nothing changes before then. To keep zero commission, subscribe again once this period ends.';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{venueName ? `Plan: ${venueName}` : 'Plan'}</DialogTitle>
          <DialogDescription>
            {locked
              ? 'Your plan is set for the length of this offer. Nothing to pay, nothing to change.'
              : onSubscription
                ? 'You are on the fixed subscription. No commission is taken on bookings.'
                : 'You are on the commission plan. Switch to a fixed fee to keep every rupee you earn.'}
          </DialogDescription>
        </DialogHeader>

        {loadingFirst ? (
          <div className="py-10 grid place-items-center">
            <Loader2 className="w-6 h-6 animate-spin text-flup-brand" aria-label="Loading plan" />
          </div>
        ) : !current ? (
          <Surface padding="p-4" className="bg-flup-surface-alt">
            <Text variant="body" tone="secondary" as="p">
              {venuePlanResponse?.error || 'Could not load this venue’s plan.'}
            </Text>
          </Surface>
        ) : (
          <>
            {/* What is true right now */}
            <Surface padding="p-4" className="bg-flup-surface-alt space-y-2">
              <div className="flex items-center justify-between gap-3">
                <Text variant="body" tone="secondary" as="span">
                  Current plan
                </Text>
                <Text variant="bodySemi" as="span">
                  {planLabel(current.plan)}
                  {/* A granted plan has no cycle to speak of — it runs to a date. */}
                  {!locked && current.billingCycle ? ` · ${current.billingCycle}` : ''}
                </Text>
              </div>

              {current.status !== 'ACTIVE' ? (
                <div className="flex items-center justify-between gap-3">
                  <Text variant="body" tone="secondary" as="span">
                    Status
                  </Text>
                  <Text variant="bodySemi" tone="warning" as="span">
                    {current.status.replace(/_/g, ' ').toLowerCase()}
                  </Text>
                </div>
              ) : null}

              {current.currentPeriodEnd ? (
                <div className="flex items-center justify-between gap-3">
                  <Text variant="body" tone="secondary" as="span">
                    Renews on
                  </Text>
                  <Text variant="bodySemi" as="span" className="nums">
                    {formatDate(current.currentPeriodEnd)}
                  </Text>
                </div>
              ) : null}

              {onSubscription && !locked && current.mandate?.status !== 'ACTIVE' ? (
                <div className="flex items-start gap-2 pt-1">
                  <AlertTriangle className="w-4 h-4 text-flup-warning shrink-0 mt-0.5" aria-hidden />
                  <Text variant="caption" tone="secondary" as="p">
                    Auto-debit is {current.mandate?.status?.toLowerCase()}. Re-authorise it to keep
                    the subscription running past {formatDate(current.currentPeriodEnd)}.
                  </Text>
                </div>
              ) : null}
            </Surface>

            {/* A term we granted: nothing to pay, nothing to switch, until it ends */}
            {locked ? (
              <Surface padding="p-4" className="bg-flup-brand-soft space-y-3">
                <div className="flex items-start gap-2">
                  <Sparkles className="w-4 h-4 text-flup-brand shrink-0 mt-0.5" aria-hidden />
                  <div className="space-y-1">
                    <Text variant="labelSemi" as="p">
                      {planLabel(current.plan)}
                      {zeroCommission ? ' · 0% commission' : ''}
                    </Text>
                    {/* The server writes this sentence; it carries the date and
                        the reason, so it is shown as-is rather than rebuilt. */}
                    <Text variant="caption" tone="secondary" as="p">
                      {eligibility?.message ||
                        `Your plan is fixed until ${formatDate(
                          founding?.endsAt || eligibility?.endsAt,
                        )}. Plan changes open after that.`}
                    </Text>
                  </div>
                </div>

                <div className="space-y-1.5 pt-1">
                  {founding?.endsAt || eligibility?.endsAt ? (
                    <div className="flex items-center justify-between gap-3">
                      <Text variant="caption" tone="secondary" as="span">
                        Offer runs until
                      </Text>
                      <Text variant="labelSemi" as="span" className="nums">
                        {formatDate(founding?.endsAt || eligibility?.endsAt)}
                      </Text>
                    </div>
                  ) : null}

                  {typeof (founding?.daysRemaining ?? eligibility?.daysRemaining) === 'number' ? (
                    <div className="flex items-center justify-between gap-3">
                      <Text variant="caption" tone="secondary" as="span">
                        Days remaining
                      </Text>
                      <Text variant="labelSemi" as="span" className="nums">
                        {founding?.daysRemaining ?? eligibility?.daysRemaining}
                      </Text>
                    </div>
                  ) : null}

                  {/* What happens on its own when the term ends — the vendor is
                      told now, not surprised on the day it flips. */}
                  {founding?.revertsTo || current.underlyingPlan ? (
                    <div className="flex items-center justify-between gap-3">
                      <Text variant="caption" tone="secondary" as="span">
                        Then moves to
                      </Text>
                      <Text variant="labelSemi" as="span">
                        {planLabel(founding?.revertsTo || current.underlyingPlan)}
                      </Text>
                    </div>
                  ) : null}
                </div>
              </Surface>
            ) : null}

            {/* A switch already booked in */}
            {pending ? (
              <Surface padding="p-4" className="bg-flup-warning-soft space-y-2">
                <div className="flex items-start gap-2">
                  <Clock className="w-4 h-4 text-flup-warning shrink-0 mt-0.5" aria-hidden />
                  <Text variant="caption" tone="secondary" as="p">
                    Moving to {pending.toPlan === 'COMMISSION' ? 'pay per booking' : 'zero commission'}
                    {pending.effectiveAt ? ` on ${formatDate(pending.effectiveAt)}` : ' shortly'}.
                    {pending.mandateCancelled ? ' Auto-debit is already off.' : ''}
                  </Text>
                </div>
                {/* No undo. Cancelling the switch would leave the venue on a
                    subscription with a dead mandate — nothing to renew it and
                    nothing scheduled to move it off — so the way back onto zero
                    commission is a fresh subscription once this period is over. */}
                {pending.toPlan === 'COMMISSION' ? (
                  <Text variant="caption" tone="secondary" as="p" className="pl-6">
                    {keepZeroCommissionNote}
                  </Text>
                ) : null}
              </Surface>
            ) : null}

            {awaitingPayment ? (
              <Surface padding="p-4" className="bg-flup-warning-soft">
                <div className="flex items-start gap-2">
                  <Clock className="w-4 h-4 text-flup-warning shrink-0 mt-0.5" aria-hidden />
                  <Text variant="caption" tone="secondary" as="p">
                    Your last payment was not completed. Pay below to finish switching to zero
                    commission — you will not be charged twice.
                  </Text>
                </div>
              </Surface>
            ) : null}

            {/* Moving onto the subscription */}
            {!onSubscription && !pending && !locked ? (
              <div className="space-y-3">
                <Text variant="labelSemi" as="p">
                  Switch to zero commission
                </Text>
                <div className="grid grid-cols-2 gap-2">
                  {cycles.map((cycle) => (
                    <button
                      key={cycle.billingCycle}
                      type="button"
                      onClick={() => setBillingCycle(cycle.billingCycle)}
                      className={cycleButton(billingCycle === cycle.billingCycle)}
                    >
                      <Text
                        variant="labelSemi"
                        as="p"
                        tone={cycle.discountPercent ? 'green' : 'primary'}
                        className="capitalize"
                      >
                        {cycle.billingCycle}
                        {cycle.discountPercent ? ` · save ${cycle.discountPercent}%` : ''}
                      </Text>
                      <Text variant="caption" tone="secondary" as="p" className="nums mt-0.5">
                        {formatPaise(cycle.netPricePaise)} /{' '}
                        {cycle.durationInMonths === 12 ? 'year' : 'month'}
                      </Text>
                      {cycle.gstPaise ? (
                        <Text variant="micro" tone="tertiary" as="p" className="nums mt-0.5">
                          + {formatPaise(cycle.gstPaise)} GST
                        </Text>
                      ) : null}
                    </button>
                  ))}
                </div>
                <Text variant="caption" tone="tertiary" as="p">
                  {formatPaise(selectedCycle?.payablePaise)}
                  {selectedCycle?.gstPaise ? ' including GST' : ''} is debited now by UPI Autopay,
                  then every {selectedCycle?.durationInMonths === 12 ? 'year' : 'month'}.
                </Text>
              </div>
            ) : null}

            {/* Leaving the subscription */}
            {onSubscription && !pending && !locked ? (
              <div className="space-y-3">
                <Text variant="labelSemi" as="p">
                  Switch to pay per booking
                </Text>

                {eligibility?.canSwitch ? (
                  <>
                    <Text variant="caption" tone="secondary" as="p">
                      {plans?.commission?.commissionPercent}% will be deducted per booking.
                      {current.currentPeriodEnd
                        ? ` Your subscription is served in full until ${formatDate(
                            current.currentPeriodEnd,
                          )}.`
                        : ''}
                    </Text>
                    <Textarea
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Why are you switching? (optional)"
                      rows={2}
                    />
                  </>
                ) : (
                  <Surface padding="p-4" className="bg-flup-surface-alt">
                    <div className="flex items-start gap-2">
                      <Clock className="w-4 h-4 text-flup-ink-3 shrink-0 mt-0.5" aria-hidden />
                      <Text variant="caption" tone="secondary" as="p">
                        You can switch from {formatDate(eligibility?.opensAt)}
                        {typeof eligibility?.daysUntilOpen === 'number'
                          ? ` — ${eligibility.daysUntilOpen} days away`
                          : ''}
                        {eligibility?.windowDays
                          ? `, ${eligibility.windowDays} days before your subscription ends on ${formatDate(
                              eligibility?.currentPeriodEnd,
                            )}.`
                          : '.'}
                      </Text>
                    </div>
                  </Surface>
                )}
              </div>
            ) : null}

            {!pending && !locked ? (
              <DialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
                {onSubscription ? (
                  <Button
                    fullWidth
                    variant="secondary"
                    disabled={busy || !eligibility?.canSwitch}
                    onClick={goToCommission}
                  >
                    {busy ? 'Working…' : 'Switch to pay per booking'}
                  </Button>
                ) : (
                  <Button fullWidth disabled={busy || !selectedCycle} onClick={goToSubscription}>
                    {busy ? 'Working…' : (
                      <>
                        <Check className="w-4 h-4 mr-2" aria-hidden />
                        Pay {formatPaise(selectedCycle?.payablePaise)} and switch
                      </>
                    )}
                  </Button>
                )}
              </DialogFooter>
            ) : null}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PlanSwitcherModal;
